import { supabase } from '../supabase';
import { mapRowToThought, THOUGHT_READ_COLUMNS } from './thoughtService';

// Get the current user ID from cached session (no network request)
const getUserId = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) {
    throw new Error('User not authenticated. Please sign in to manage collections.');
  }
  return session.user.id;
};

// Map a Supabase collection row to the app's collection object shape
const mapCollectionRow = (r) => ({
  id: r.id,
  name: r.name,
  description: r.description || '',
  userId: r.user_id,
  isDefault: !!r.is_default,
  isTagCollection: !!r.is_tag_collection,
  tagName: r.tag_name || '',
  createdAt: r.created_at,
  updatedAt: r.updated_at,
});

// Get all collections for the current user
export const getCollections = async () => {
  const userId = await getUserId();

  try {
    const { data, error } = await supabase
      .from('collections')
      .select('*')
      .eq('user_id', userId)
      .order('name', { ascending: true });

    if (error) throw error;
    return (data || []).map(mapCollectionRow);
  } catch (error) {
    console.error('Error getting collections:', error);
    return [];
  }
};

// Create a new collection
export const createCollection = async (name, description = '') => {
  const userId = await getUserId();

  try {
    const { data, error } = await supabase
      .from('collections')
      .insert([{
        name,
        description,
        user_id: userId,
        is_default: false,
      }])
      .select('id')
      .single();

    if (error) throw error;
    return data.id;
  } catch (error) {
    console.error('Error creating collection:', error);
    throw error;
  }
};

// Update a collection's metadata
export const updateCollection = async (collectionId, updates) => {
  const userId = await getUserId();

  try {
    const row = {};
    if (updates.name !== undefined) row.name = updates.name;
    if (updates.description !== undefined) row.description = updates.description;

    const { error } = await supabase
      .from('collections')
      .update(row)
      .eq('id', collectionId)
      .eq('user_id', userId);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating collection:', error);
    throw error;
  }
};

// Delete a collection (collection_items cascade automatically via ON DELETE CASCADE)
export const deleteCollection = async (collectionId) => {
  const userId = await getUserId();

  try {
    const { error } = await supabase
      .from('collections')
      .delete()
      .eq('id', collectionId)
      .eq('user_id', userId);
    if (error) throw error;
  } catch (error) {
    console.error('Error deleting collection:', error);
    throw error;
  }
};

// Add a thought to a collection (uses upsert to avoid duplicate-check round trip)
export const addThoughtToCollection = async (collectionId, thoughtId) => {
  const userId = await getUserId();

  try {
    const { error } = await supabase
      .from('collection_items')
      .upsert({
        collection_id: collectionId,
        thought_id: thoughtId,
        user_id: userId,
        position: 0,
      }, { onConflict: 'collection_id,thought_id', ignoreDuplicates: true });

    if (error) throw error;
  } catch (error) {
    console.error('Error adding thought to collection:', error);
    throw error;
  }
};

// Add multiple thoughts to a collection
export const addThoughtsToCollectionBatch = async (collectionId, thoughtIds) => {
  if (!thoughtIds.length) return;
  const userId = await getUserId();

  try {
    const rows = thoughtIds.map(thoughtId => ({
      collection_id: collectionId,
      thought_id: thoughtId,
      user_id: userId,
      position: 0,
    }));

    const { error } = await supabase
      .from('collection_items')
      .upsert(rows, { onConflict: 'collection_id,thought_id', ignoreDuplicates: true });

    if (error) throw error;
  } catch (error) {
    console.error('Error adding thoughts to collection in batch:', error);
    throw error;
  }
};

// Remove a thought from a collection
export const removeThoughtFromCollection = async (collectionId, thoughtId) => {
  const userId = await getUserId();

  try {
    const { error } = await supabase
      .from('collection_items')
      .delete()
      .eq('collection_id', collectionId)
      .eq('thought_id', thoughtId)
      .eq('user_id', userId);

    if (error) throw error;
  } catch (error) {
    console.error('Error removing thought from collection:', error);
    throw error;
  }
};

// Remove multiple thoughts from a collection
export const removeThoughtsFromCollectionBatch = async (collectionId, thoughtIds) => {
  if (!thoughtIds.length) return;
  const userId = await getUserId();

  try {
    const { error } = await supabase
      .from('collection_items')
      .delete()
      .eq('collection_id', collectionId)
      .in('thought_id', thoughtIds)
      .eq('user_id', userId);

    if (error) throw error;
  } catch (error) {
    console.error('Error removing thoughts from collection in batch:', error);
    throw error;
  }
};

// Get all thoughts in a collection
export const getCollectionThoughts = async (collectionId) => {
  const userId = await getUserId();

  try {
    // Check if this is a tag collection
    const { data: coll, error: collError } = await supabase
      .from('collections')
      .select('*')
      .eq('id', collectionId)
      .eq('user_id', userId)
      .single();

    if (collError) throw collError;
    if (!coll) return [];

    // If tag collection, use tag-based query
    if (coll.is_tag_collection && coll.tag_name) {
      return getThoughtsByTag(coll.tag_name);
    }

    // Use a join to fetch thoughts via collection_items in a single query.
    // The embedded select uses THOUGHT_READ_COLUMNS (not '*') to avoid pulling
    // the giant `embedding` column — see thoughtService.js for context.
    const { data, error } = await supabase
      .from('collection_items')
      .select(`thought_id, thoughts(${THOUGHT_READ_COLUMNS})`)
      .eq('collection_id', collectionId)
      .eq('user_id', userId);

    if (error) throw error;
    return (data || []).map(item => mapRowToThought(item.thoughts)).filter(Boolean);
  } catch (error) {
    console.error('Error getting collection thoughts:', error);
    return [];
  }
};

// Get all thoughts with a specific tag
export const getThoughtsByTag = async (tagName) => {
  const userId = await getUserId();

  try {
    // THOUGHT_READ_COLUMNS (not '*') to skip the giant `embedding` column —
    // see thoughtService.js for context.
    const { data, error } = await supabase
      .from('thoughts')
      .select(THOUGHT_READ_COLUMNS)
      .eq('user_id', userId)
      .contains('tags', [tagName]);

    if (error) throw error;
    return (data || []).map(mapRowToThought);
  } catch (error) {
    console.error(`Error getting thoughts with tag ${tagName}:`, error);
    return [];
  }
};

// Ensure default collection exists
export const ensureDefaultCollection = async () => {
  const userId = await getUserId();

  try {
    const { data: existing } = await supabase
      .from('collections')
      .select('id')
      .eq('user_id', userId)
      .eq('is_default', true)
      .limit(1);

    if (existing && existing.length > 0) return;

    const { error } = await supabase
      .from('collections')
      .insert([{
        name: 'Your Thoughts',
        description: 'All your thoughts in one place',
        user_id: userId,
        is_default: true,
      }]);

    if (error) throw error;
  } catch (error) {
    console.error('Error ensuring default collection:', error);
    throw error;
  }
};

// Get all collections that contain a specific thought (uses join)
export const getCollectionsForThought = async (thoughtId) => {
  const userId = await getUserId();

  try {
    const { data, error } = await supabase
      .from('collection_items')
      .select('collection_id, collections(*)')
      .eq('thought_id', thoughtId)
      .eq('user_id', userId);

    if (error) throw error;
    if (!data || data.length === 0) return [];

    const mapped = data.map(item => mapCollectionRow(item.collections)).filter(Boolean);

    // Sort by default first, then by name
    return mapped.sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return a.name.localeCompare(b.name);
    });
  } catch (error) {
    console.error('Error getting collections for thought:', error);
    return [];
  }
};

// Add a thought to multiple collections
export const addThoughtToCollectionsBatch = async (thoughtId, collectionIds) => {
  if (!collectionIds.length) return;
  const userId = await getUserId();

  try {
    const rows = collectionIds.map(collectionId => ({
      collection_id: collectionId,
      thought_id: thoughtId,
      user_id: userId,
      position: 0,
    }));

    const { error } = await supabase
      .from('collection_items')
      .upsert(rows, { onConflict: 'collection_id,thought_id', ignoreDuplicates: true });

    if (error) throw error;
  } catch (error) {
    console.error('Error adding thought to collections in batch:', error);
    throw error;
  }
};

// Remove a thought from multiple collections
export const removeThoughtFromCollectionsBatch = async (thoughtId, collectionIds) => {
  if (!collectionIds.length) return;
  const userId = await getUserId();

  try {
    const { error } = await supabase
      .from('collection_items')
      .delete()
      .eq('thought_id', thoughtId)
      .in('collection_id', collectionIds)
      .eq('user_id', userId);

    if (error) throw error;
  } catch (error) {
    console.error('Error removing thought from collections in batch:', error);
    throw error;
  }
};

// Create or get a tag collection - returns collection ID
export const getOrCreateTagCollection = async (tagName) => {
  const userId = await getUserId();

  try {
    const { data: existing } = await supabase
      .from('collections')
      .select('id')
      .eq('user_id', userId)
      .eq('is_tag_collection', true)
      .eq('tag_name', tagName)
      .limit(1);

    if (existing && existing.length > 0) {
      return existing[0].id;
    }

    const { data, error } = await supabase
      .from('collections')
      .insert([{
        name: `/${tagName}`,
        description: `Thoughts tagged with /${tagName}`,
        user_id: userId,
        is_default: false,
        is_tag_collection: true,
        tag_name: tagName,
      }])
      .select('id')
      .single();

    if (error) throw error;
    return data.id;
  } catch (error) {
    console.error(`Error creating tag collection for ${tagName}:`, error);
    throw error;
  }
};

// Sync a thought's tag collections — add new ones and remove stale ones
export const syncThoughtTagCollections = async (thought) => {
  const currentTags = thought.tags || [];

  try {
    const existingCollections = await getCollectionsForThought(thought.id);
    const existingTagCollections = existingCollections.filter(c => c.isTagCollection);

    const existingTagNames = new Set(existingTagCollections.map(c => c.tagName));
    const desiredTagNames = new Set(currentTags);

    // Remove from tag collections whose tag is no longer on the thought
    const toRemove = existingTagCollections.filter(c => !desiredTagNames.has(c.tagName));
    if (toRemove.length > 0) {
      await removeThoughtFromCollectionsBatch(thought.id, toRemove.map(c => c.id));
    }

    // Add to tag collections for new tags
    const tagsToAdd = currentTags.filter(tag => !existingTagNames.has(tag));
    for (const tag of tagsToAdd) {
      const collectionId = await getOrCreateTagCollection(tag);
      await addThoughtToCollection(collectionId, thought.id);
    }
  } catch (error) {
    console.error('Error syncing thought tag collections:', error);
    throw error;
  }
};
